import React, { ReactElement, useMemo, useRef, useState } from "react";
import { Avatar, Input, Skeleton, Upload } from "antd";
import type { GetProp, UploadProps } from "antd";
import { useTranslate } from "@obsbot/remo-i18n";
import {
    AvatarBackgroundColor,
    eventBus,
    filterInputValue,
    limitInputLength,
    useGenerateAvatar,
} from "@obsbot/remo-utils";
import { withMessage } from "../../HOC/withMessage";
import { CustomModal } from "../CustomModal";
import "./index.less";

export interface SetAccountProps {
    visible: boolean;
    username: string;
    userAvatar: string;
    onConfirm: (username: string, avatar: string) => void;
    setVisible: (flag: boolean) => void;
    handleUpload: (file: any) => Promise<any>;
}

export type FileType = Parameters<GetProp<UploadProps, "beforeUpload">>[0];

export const AvatarBackgroundMap: Record<number, AvatarBackgroundColor> = {
    1: "#4F72FF",
    2: "#744CF6",
    3: "#D74CF6",
    4: "#FFA44F",
};

export const SetAccountModal = withMessage<SetAccountProps>(
    ({
        visible,
        username,
        message,
        userAvatar,
        onConfirm,
        setVisible,
        handleUpload,
    }): ReactElement => {
        const t = useTranslate();
        // 是否正在上传的loading
        const [isLoading, setIsLoading] = useState(false);
        // 实时记录当前input的用户名
        const [usernameIpt, setUsernameIpt] = useState(username);
        // 记录当前上传的图片URL地址
        const [avatarUrl, setAvatarUrl] = useState("");
        const defaultAvatarBackgroundIndex = useMemo(() => {
            const match = userAvatar.match(/default_(\d+)\.png$/);
            const index = match ? parseInt(match[1], 10) : 1;
            return index;
        }, [userAvatar]);
        const defaultAvatar = useGenerateAvatar(
            usernameIpt,
            AvatarBackgroundMap[defaultAvatarBackgroundIndex],
        );
        // 是否正在拼音
        const isComposing = useRef(false);
        const defaultAvatarURL = useMemo(() => {
            return defaultAvatar.toDataURL();
        }, [usernameIpt]);
        // 选择完文件后, 准备上传的回调函数
        const beforeUpload = (file: FileType): boolean => {
            if (isLoading) {
                message.error(t("uploadLimit.uploading"));
                return false;
            }
            const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png";
            console.log("查看当前上传的文件类型", file);
            if (!isJpgOrPng) {
                message.error(t("uploadLimit.picType"));
                return false;
            }
            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                message.error(t("uploadLimit.sizeLimit"));
                return false;
            }
            return isJpgOrPng && isLt5M;
        };
        // 上传组件的change事件
        const handleChange = async (options: any): Promise<void> => {
            console.log("handleChange options", options);
            setIsLoading(true);
            try {
                const formData = new FormData();
                formData.append("files", options.file);
                const res = await handleUpload(formData);
                console.log("handleChange res", res);
                if (res?.[0]?.url) {
                    // 先预加载图片
                    const img = new window.Image();
                    img.src = res[0].url;
                    img.onload = () => {
                        setAvatarUrl(res[0].url);
                        setIsLoading(false);
                    };
                    img.onerror = () => {
                        setIsLoading(false);
                    };
                }
                setIsLoading(false);
            } catch (error) {
                setIsLoading(false);
            }
        };
        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
            if (e.key === "Enter") {
                handleConfirm();
            }
        };
        const handleConfirm = async (): Promise<void> => {
            if (isLoading) {
                message.error(t("uploadLimit.uploading"));
                return;
            }
            if (!usernameIpt) {
                message.warning(t("noInputSingle", { text1: t("username") }));
                return;
            }
            const getCanvasBlob = (): Promise<Blob | null> => {
                return new Promise(resolve => {
                    defaultAvatar.toBlob(blob => resolve(blob), "image/png");
                });
            };
            let logoURL = avatarUrl;
            if (!logoURL) {
                try {
                    setIsLoading(true);
                    const blob = await getCanvasBlob();
                    if (!blob) {
                        console.error("[WEB] ERROR 获取canvas的blob失败", defaultAvatar);
                        return;
                    }
                    const formData = new FormData();
                    formData.append("files", blob, `default_${defaultAvatarBackgroundIndex}-.png`);
                    const res = await handleUpload(formData);
                    if (res?.[0]?.url) {
                        setAvatarUrl(res[0].url);
                        logoURL = res[0].url;
                    }
                    setIsLoading(true);
                } catch (error) {
                    setIsLoading(true);
                }
            }
            onConfirm && onConfirm(usernameIpt, logoURL);
        };
        return (
            <CustomModal
                className="SetAccountModal"
                footer={
                    <div className="account-modal-button-item" onClick={handleConfirm}>
                        {t("confirm")}
                    </div>
                }
                open={visible}
                renderHeader={""}
                setVisible={setVisible}
                width={392}
                onClickClose={() => {
                    setUsernameIpt(username);
                    handleConfirm();
                }}
            >
                <div className="account-modal-form-upload">
                    <Upload
                        accept=".jpg,.png,.jpeg"
                        beforeUpload={beforeUpload}
                        className="account-upload-com"
                        customRequest={handleChange}
                        maxCount={1}
                        name="avatar"
                        showUploadList={false}
                    >
                        <div className="account-upload-section">
                            <div className="account-upload-btn">
                                {isLoading ? (
                                    <Skeleton.Avatar active={true} shape="circle" size={76} />
                                ) : (
                                    <Avatar src={avatarUrl || defaultAvatarURL} />
                                )}
                            </div>
                            <div className="account-upload-icon">
                                <svg
                                    fill="none"
                                    height="11.111328125"
                                    version="1.1"
                                    viewBox="0 0 11.111328125 11.111328125"
                                    width="11.111328125"
                                    xmlns="http://www.w3.org/2000/svg"
                                    xmlnsXlink="http://www.w3.org/1999/xlink"
                                >
                                    <g>
                                        <path
                                            d="M4.4443359,6.6675348L4.4443359,10Q4.4443359,10.054586,4.4496865,10.108908Q4.4550366,10.163231,4.4656854,10.216766Q4.4763346,10.270304,4.4921799,10.322538Q4.5080252,10.374773,4.528914,10.425202Q4.5498028,10.475634,4.5755343,10.523774Q4.6012659,10.571914,4.6315913,10.617299Q4.6619177,10.662685,4.6965466,10.70488Q4.7311754,10.747076,4.7697725,10.785673Q4.8083706,10.824272,4.8505654,10.8589Q4.8927603,10.893529,4.9381466,10.923854Q4.9835329,10.954181,5.031673,10.979912Q5.079813,11.005643,5.1302433,11.026531Q5.1806741,11.047421,5.2329092,11.063267Q5.2851439,11.079112,5.3386803,11.089761Q5.3922167,11.10041,5.4465394,11.105762Q5.5008621,11.111111,5.5554471,11.111111Q5.610033,11.111111,5.6643553,11.105761Q5.7186775,11.10041,5.7722139,11.089761Q5.8257508,11.079112,5.8779855,11.063266Q5.9302206,11.047421,5.9806504,11.026531Q6.0310812,11.005643,6.0792208,10.979912Q6.1273608,10.954181,6.1727467,10.923854Q6.218133,10.893529,6.2603288,10.8589Q6.3025236,10.824272,6.3411212,10.785673Q6.3797193,10.747076,6.4143476,10.704881Q6.4489765,10.662685,6.4793019,10.617299Q6.5096283,10.571914,6.5353603,10.523773Q6.5610914,10.475634,6.5819798,10.425202Q6.602869,10.374773,6.6187143,10.322538Q6.6345596,10.270304,6.6452084,10.216766Q6.655858,10.163231,6.6612082,10.108908Q6.6665587,10.054586,6.6665583,10L6.6665583,6.6675348L10.000217,6.6675348Q10.054803,6.6675348,10.109125,6.6621847Q10.163448,6.6568341,10.216984,6.6461844Q10.270521,6.6355357,10.322755,6.6196904Q10.37499,6.6038451,10.42542,6.5829558Q10.475851,6.562067,10.523991,6.5363359Q10.57213,6.5106044,10.617517,6.480278Q10.662903,6.4499521,10.705097,6.4153237Q10.747293,6.3806953,10.785891,6.3420973Q10.824489,6.3035002,10.859118,6.2613049Q10.893746,6.2191095,10.924072,6.1737232Q10.954399,6.1283374,10.98013,6.0801973Q11.005861,6.0320578,11.02675,5.981627Q11.047639,5.9311972,11.063484,5.8789625Q11.07933,5.8267274,11.089979,5.7731905Q11.100628,5.7196541,11.105978,5.6653318Q11.111328,5.6110096,11.111328,5.5564237Q11.111328,5.5018382,11.105978,5.4475155Q11.100628,5.3931928,11.089977,5.3396559Q11.079329,5.2861195,11.063484,5.2338848Q11.047639,5.1816502,11.02675,5.1312194Q11.005861,5.0807896,10.980129,5.0326495Q10.954398,4.9845095,10.924071,4.9391232Q10.893745,4.8937368,10.859118,4.851542Q10.824489,4.8093472,10.785891,4.7707491Q10.747293,4.732152,10.705097,4.6975231Q10.662903,4.6628947,10.617516,4.6325684Q10.57213,4.6022425,10.523991,4.5765114Q10.475851,4.5507798,10.42542,4.5298905Q10.37499,4.5090017,10.322755,4.4931564Q10.270521,4.4773111,10.216984,4.4666619Q10.163448,4.4560132,10.109125,4.4506631Q10.054803,4.4453125,10.000217,4.4453125L6.6665583,4.4453125L6.6665583,1.1111112Q6.6665587,1.0565258,6.6612086,1.0022032Q6.655858,0.94788063,6.6452084,0.89434403Q6.6345596,0.84080756,6.6187143,0.78857255Q6.602869,0.7363376,6.5819798,0.68590724Q6.5610914,0.63547695,6.5353599,0.58733696Q6.5096283,0.53919697,6.4793019,0.49381083Q6.448976,0.44842476,6.4143476,0.40622967Q6.3797188,0.36403462,6.3411207,0.32543692Q6.3025236,0.28683922,6.2603283,0.25221062Q6.218133,0.21758199,6.1727467,0.18725596Q6.1273608,0.15692997,6.0792208,0.13119859Q6.0310807,0.10546722,5.9806499,0.084578291Q5.9302201,0.063689373,5.877985,0.047844071Q5.8257504,0.031998768,5.7722139,0.021349687Q5.7186775,0.010700608,5.6643553,0.0053503038Q5.610033,0,5.5554471,0Q5.5008621,0,5.4465394,0.0053503038Q5.3922167,0.010700608,5.3386793,0.021349687Q5.2851434,0.031998768,5.2329082,0.047844071Q5.1806736,0.063689373,5.1302428,0.084578291Q5.079813,0.10546722,5.031673,0.1311986Q4.9835329,0.15692997,4.9381466,0.18725598Q4.8927603,0.217582,4.8505654,0.25221062Q4.8083706,0.28683922,4.7697725,0.32543692Q4.7311754,0.36403462,4.6965466,0.4062297Q4.6619177,0.44842476,4.6315913,0.49381083Q4.6012654,0.53919697,4.5755339,0.58733696Q4.5498028,0.63547695,4.5289135,0.68590724Q4.5080252,0.7363376,4.4921799,0.78857255Q4.4763346,0.84080756,4.4656854,0.89434409Q4.4550366,0.94788069,4.4496865,1.0022032Q4.4443359,1.0565258,4.4443359,1.1111112L4.4443359,4.4453125L1.1113291,4.4453125Q1.0567437,4.4453125,1.0024211,4.4506631Q0.94809854,4.4560137,0.89456189,4.4666624Q0.84102535,4.4773116,0.78879035,4.4931569Q0.73655534,4.5090017,0.68612498,4.5298905Q0.63569474,4.5507798,0.58755469,4.5765114Q0.53941464,4.6022425,0.49402845,4.6325684Q0.44864237,4.6628947,0.40644729,4.6975231Q0.36425221,4.732152,0.32565448,4.7707491Q0.28705677,4.8093472,0.25242814,4.851542Q0.21779953,4.8937368,0.18747349,4.9391232Q0.15714747,4.9845095,0.13141608,5.0326495Q0.1056847,5.0807896,0.084795766,5.1312194Q0.063906834,5.1816502,0.048061531,5.2338848Q0.032216221,5.2861195,0.021567134,5.3396559Q0.010918049,5.3931928,0.0055677439,5.4475155Q0.00021743774,5.5018382,0.00021743774,5.5564237Q0.00021743774,5.6110096,0.005567743,5.6653318Q0.010918048,5.7196541,0.021567132,5.7731905Q0.032216217,5.8267274,0.048061527,5.878962Q0.063906834,5.9311972,0.084795758,5.981627Q0.1056847,6.0320578,0.13141608,6.0801973Q0.15714747,6.1283374,0.18747348,6.1737232Q0.21779951,6.2191095,0.25242814,6.2613053Q0.28705677,6.3035002,0.32565448,6.3420973Q0.36425221,6.3806953,0.40644729,6.4153237Q0.44864237,6.4499521,0.49402845,6.480278Q0.53941464,6.5106044,0.58755469,6.5363359Q0.63569474,6.562067,0.68612504,6.5829558Q0.73655546,6.6038451,0.7887904,6.6196904Q0.84102535,6.6355357,0.89456189,6.6461844Q0.94809854,6.6568341,1.0024211,6.6621847Q1.0567437,6.6675348,1.1113291,6.6675348L4.4443359,6.6675348Z"
                                            fill="#FFFFFF"
                                            fillOpacity="1"
                                            fillRule="evenodd"
                                        />
                                    </g>
                                </svg>
                            </div>
                        </div>
                    </Upload>
                </div>
                <div className="account-modal-title">{t("customAccount")}</div>
                <div className="account-modal-form-input">
                    <div className="account-modal-form-input-title">{t("username")}</div>
                    <div className="account-modal-form-input-section">
                        <Input
                            className="account-modal-form-input-com"
                            placeholder={t("usernamePlaceholder")}
                            value={usernameIpt}
                            onChange={e => {
                                let filterVal = filterInputValue(e.target.value);
                                if (!isComposing.current) {
                                    const { val, overLimit } = limitInputLength(filterVal);
                                    if (overLimit) {
                                        eventBus.emit("show-message", {
                                            message: t("nameLengthOverLimit"),
                                            type: "warning",
                                        });
                                    }
                                    filterVal = val;
                                }
                                setUsernameIpt(filterVal);
                            }}
                            onCompositionEnd={e => {
                                isComposing.current = false;
                                const finalValue = (e.target as any).value;
                                const { val, overLimit } = limitInputLength(finalValue);
                                if (overLimit) {
                                    eventBus.emit("show-message", {
                                        message: t("nameLengthOverLimit"),
                                        type: "warning",
                                    });
                                }
                                setUsernameIpt(val);
                            }}
                            onCompositionStart={() => (isComposing.current = true)}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                </div>
            </CustomModal>
        );
    },
);
